<template>
  <Teleport to="body">
    <div class="confirm-overlay" @click.self="cancel">
      <div
        class="confirm-panel"
        role="alertdialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="messageId"
      >
        <h3 :id="titleId" class="confirm-title">{{ title }}</h3>
        <p :id="messageId" class="confirm-message">{{ message }}</p>
        <div class="confirm-actions">
          <button
            ref="cancelBtn"
            type="button"
            class="btn btn-secondary"
            @click="cancel"
          >
            {{ cancelLabel }}
          </button>
          <button
            type="button"
            class="btn"
            :class="variant === 'danger' ? 'btn-danger' : 'btn-primary'"
            @click="confirm"
          >
            {{ confirmLabel }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
let _uid = 0;
const nextId = () => `confirm-dialog-${++_uid}`;

export default {
  name: 'ConfirmDialog',
  props: {
    title: { type: String, required: true },
    message: { type: String, required: true },
    confirmLabel: { type: String, required: true },
    cancelLabel: { type: String, required: true },
    // 'danger' (default) → red confirm button; 'primary' → green confirm button.
    variant: {
      type: String,
      default: 'danger',
      validator: v => ['danger', 'primary'].includes(v),
    },
  },
  emits: ['confirm', 'cancel'],
  data() {
    return {
      titleId: nextId(),
      messageId: nextId(),
      previouslyFocusedElement: null,
    };
  },
  mounted() {
    this.previouslyFocusedElement = document.activeElement;
    document.addEventListener('keydown', this.onKeydown);
    // Focus on Cancel by default — for destructive actions, Enter shouldn't
    // accidentally trigger Confirm. The user must explicitly click Confirm.
    this.$nextTick(() => {
      if (this.$refs.cancelBtn) this.$refs.cancelBtn.focus();
    });
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
    const el = this.previouslyFocusedElement;
    if (el && typeof el.focus === 'function') el.focus();
  },
  methods: {
    onKeydown(e) {
      if (e.key === 'Escape') this.cancel();
    },
    cancel() {
      this.$emit('cancel');
    },
    confirm() {
      this.$emit('confirm');
    },
  },
};
</script>

<style scoped>
.confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;
}

.confirm-panel {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  max-width: 420px;
  width: 100%;
  padding: 24px;
  box-sizing: border-box;
}

.confirm-title {
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
}

.confirm-message {
  margin: 0 0 20px 0;
  font-size: 14px;
  color: #2c3e50;
  line-height: 1.5;
}

.confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: 9px 20px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
  letter-spacing: 0.3px;
}

.btn-secondary {
  background: #fff;
  color: #2c3e50;
  border: 1px solid #ced4da;
}
.btn-secondary:hover { background: #f1f3f5; }
.btn-secondary:focus-visible { outline: 2px solid #42b983; outline-offset: 2px; }

.btn-primary { background: #42b983; color: #fff; }
.btn-primary:hover { background: #36a273; }
.btn-primary:focus-visible { outline: 2px solid #2E7D32; outline-offset: 2px; }

.btn-danger { background: #e74c3c; color: #fff; }
.btn-danger:hover { background: #c0392b; }
.btn-danger:focus-visible { outline: 2px solid #c0392b; outline-offset: 2px; }

@media (max-width: 480px) {
  .confirm-actions {
    flex-direction: column-reverse;
  }
  .confirm-actions .btn {
    width: 100%;
  }
}
</style>
